import { useEffect, useRef } from "react";
import { getVapidPublicKey, subscribePush } from "../lib/notificationApi";

const PUSH_REGISTERED_ENDPOINT_KEY = "push_registered_endpoint";

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

async function registerSubscriptionWithBackend(endpoint: string, keys: { auth: string; p256dh: string }) {
  await subscribePush({ endpoint, keys });
  localStorage.setItem(PUSH_REGISTERED_ENDPOINT_KEY, endpoint);
}

export function usePushNotification() {
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

    attempted.current = true;

    (async () => {
      try {
        const registration = await navigator.serviceWorker.ready;

        if (Notification.permission === "denied") return;

        const existingSub = await registration.pushManager.getSubscription();

        if (existingSub) {
          // Only re-register if the endpoint changed (e.g. browser rotated it)
          const registeredEndpoint = localStorage.getItem(PUSH_REGISTERED_ENDPOINT_KEY);
          if (registeredEndpoint === existingSub.endpoint) return;

          const json = existingSub.toJSON();
          if (json.endpoint && json.keys) {
            await registerSubscriptionWithBackend(json.endpoint, {
              auth: json.keys["auth"] ?? "",
              p256dh: json.keys["p256dh"] ?? "",
            }).catch(() => undefined);
          }
          return;
        }

        // No subscription yet — ask for permission and create one
        if (Notification.permission === "default") {
          const permission = await Notification.requestPermission();
          if (permission !== "granted") return;
        }

        const vapidPublicKey = await getVapidPublicKey();
        if (!vapidPublicKey) return;

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });

        const json = subscription.toJSON();
        if (!json.endpoint || !json.keys) return;

        await registerSubscriptionWithBackend(json.endpoint, {
          auth: json.keys["auth"] ?? "",
          p256dh: json.keys["p256dh"] ?? "",
        });
      } catch (err) {
        console.warn("[push] Setup failed:", err);
      }
    })();
  }, []);
}
